import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';
import styled from 'styled-components';
import { AlertSingleLine, DataCard } from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { ApplicationLayout } from '../Organisms/ApplicationLayout';
import ApplicationSteps from '../Molecules/ApplicationSteps';
import { routes } from '../Routes';
import { useCurrentApplication } from '../../services/hooks/useCurrentApplication';
import ApplicationSummary from '../Molecules/ApplicationSummary';
import { useCertificates } from '../../services/hooks/useCertificates';
import { useAuthorities } from '../../services/hooks/useAuthorities';
import { useNationalities } from '../../services/hooks/useNationalities';
import { endorsementApi } from '../../httpclient';
import { ApplicationStatus } from '../../constants/enums';

const SummaryPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { application, setApplication } = useCurrentApplication();
  const {
    result: allCertificates,
    error: allCertificatesError,
    loading: allCertificatesLoading,
  } = useCertificates();

  const {
    result: allAuthorities,
    error: allAuthoritiesError,
    loading: allAuthoritiesLoading,
  } = useAuthorities();

  const {
    result: nationalities,
    error: nationalitiesError,
    loading: nationalitiesLoading,
  } = useNationalities();

  const {
    errorObject: prepareForPaymentError,
    loading: prepareForPaymentLoading,
    callApi: prepareForPayment,
  } = useApi({
    apiFunction: (payload) => endorsementApi.prepareApplicationGroupForPayment(payload.uid),
    callApiOnInit: false,
    successCallback: (result) => {
      const resultingChargeIds = (result || []).map((c) => c?.chargeId || 0);
      if (resultingChargeIds.length > 0 && setApplication) {
        const modifiedApplication = { ...application, chargeIds: resultingChargeIds };
        setApplication({ result: modifiedApplication });
      }
      const path = generatePath(routes.payment, { applicationUid: application?.result?.uid });
      navigate(path);
    },
  });

  const handleSubmit = useCallback(() => {
    prepareForPayment({ uid: application?.result?.uid });
  }, [application?.result?.uid]);

  const disabled =
    nationalitiesLoading ||
    allCertificatesLoading ||
    allAuthoritiesLoading ||
    prepareForPaymentLoading;

  return (
    <ApplicationLayout
      canNavNext={!disabled}
      canNavBack={!disabled && application?.result?.applicationStatus === ApplicationStatus.Draft}
      heading={intl.formatMessage({ id: 'application.new.title' })}
      ingress={intl.formatMessage({ id: 'application.new.ingress' })}
      exitLabel={intl.formatMessage({ id: 'general.backtoapplications' })}
      navBackLabel={intl.formatMessage({ id: 'general.back' })}
      navNextLabel={intl.formatMessage({ id: 'general.next' })}
      timeline={<ApplicationSteps />}
      onNavNextClick={handleSubmit}
    >
      <DataCard>
        <Summary>
          <h2>{intl.formatMessage({ id: 'application.summary.title' })}</h2>
          <Content>
            <ApplicationSummary
              application={application?.result || undefined}
              authorities={allAuthorities}
              certificates={allCertificates}
              nationalities={nationalities}
            />
          </Content>
          {application?.result && (
            <AlertWrapper>
              <AlertSingleLine title="" type="info" hideClose>
                <CRAInfo>
                  <strong>{intl.formatMessage({ id: 'application.summary.crainfo.title' })}</strong>
                  <span>{intl.formatMessage({ id: 'application.summary.crainfo.part1' })}</span>
                  <FormattedMessage
                    id="application.summary.crainfo.part2"
                    tagName="span"
                    values={{
                      b: (chunks) => <b>{chunks}</b>,
                    }}
                  />
                  <span>{intl.formatMessage({ id: 'application.summary.crainfo.part3' })}</span>
                </CRAInfo>
              </AlertSingleLine>
            </AlertWrapper>
          )}
        </Summary>
      </DataCard>
      {prepareForPaymentError && (
        <ErrorWrapper>
          <ErrorMessage error={prepareForPaymentError} />
        </ErrorWrapper>
      )}
    </ApplicationLayout>
  );
};

export default SummaryPage;

const Summary = styled.div`
  padding: 10px 35px 40px 35px;
  min-height: 400px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
`;

const ErrorWrapper = styled.div`
  margin-bottom: 20px;
`;

const AlertWrapper = styled.div`
  & > div {
    align-items: flex-start;
  }
`;

const CRAInfo = styled.div`
  padding: 15px 15px 15px 0;
  strong {
    display: block;
    margin-bottom: 5px;
  }
  span:not(:last-child) {
    display: block;
    margin-bottom: 10px;
  }
`;
